import type { KycCheckStatus, KycSessionStatus, Liveness } from '@/common/types';
import type {
  ContactChannelsResponseBody,
  UpdateContactChannelsBody,
  UpdateContactChannelsProps,
  UpdateExpectedSpendBody,
  ExpectedSpendResponseBody,
  UpdateExpectedSpendProps,
  SupportedRegionsResponseBody,
  ResidentialAddressesResponseBody,
  StructuredResidentialAddressDto,
  UnstructuredResidentialAddressDto,
  AttachKycDocumentBody,
  AttachKycDocumentProps,
  UploadFileProps,
  UploadFileResponseBody,
} from './types';
import { config } from '@/config';
import { useImmersveAuthStore } from '@/stores/auth.store';
import axios, { type AxiosInstance } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { ImmersveError } from '@/common/ImmersveError';
import type { FeatureFlagKvp, KycProvider } from '@/models/KycSession';

export type KycSessionDtoHiddenSteps =
  | 'region'
  | 'expected-spend'
  | 'contact-channels'
  | 'residential-address';

export type KycSessionDtoFlowType = 'kyc-identity-verification' | 'kyc-identity-review';

export type KycSessionDtoFlowStepName =
  | 'region'
  | 'consent'
  | 'contact-channels'
  | 'expected-spend'
  | 'residential-address'
  | 'proof-of-address'
  | 'identity-verification';

export interface KycSessionDtoFlow {
  type: KycSessionDtoFlowType;
  steps: { name: KycSessionDtoFlowStepName }[];
}

export interface KycSessionDto {
  redirectUrl: string;
  id: string;
  accountId: string;
  partnerAccountId: string;
  region: string;
  externalId: string;
  createdAt: string;
  liveness: Liveness;
  status: KycSessionStatus;
  partnerLogoUrl?: string;
  partnerName?: string;
  kycProvider?: KycProvider;
  kycCheck?: { status: KycCheckStatus };
  hiddenSteps?: KycSessionDtoHiddenSteps[];
  attachments?: KycSessionAttachmentDto[];
  flags?: FeatureFlagKvp[];
  flow?: KycSessionDtoFlow;
}

export interface KycSessionAttachmentDto {
  type: string;
  fileId: string;
}

export interface ExternalSessionToken {
  token: string;
}

export class AmethystClient {
  baseUrl: string;
  axiosInstance: AxiosInstance;

  constructor() {
    const testMode = config.testModeEnabled;
    this.baseUrl = testMode ? config.immersveApiBaseUrlTest : config.immersveApiBaseUrlLive;
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      timeout: config.axiosTimeout,
    });

    axiosRetry(this.axiosInstance, {
      retries: 2,
      retryCondition: (e) => {
        return isNetworkOrIdempotentRequestError(e) || !e.status || !e.response?.status;
      },
      retryDelay: axiosRetry.exponentialDelay, // delay * 0.2 * Math.random(), initial delay is 100ms, then exponentially increases
      shouldResetTimeout: true,
    });
    this.setupInterceptors();
  }

  setupInterceptors() {
    this.axiosInstance.interceptors.request.use((cfg) => {
      const kycSession = useKycSessionStore().kycSession;
      const sessionId = kycSession?.id;
      if (sessionId) {
        cfg.headers.set('X-Trace-Id', sessionId);
      }
      return cfg;
    });
  }

  async getKycSessionData(sessionId: string): Promise<KycSessionDto> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    let data: KycSessionDto;
    try {
      const response = await this.axiosInstance<KycSessionDto>({
        method: 'GET',
        url: `/api/kyc-sessions/${sessionId}`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      data = response.data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionGetDataFailed',
        cause: err as Error,
      });
    }

    if (!data.status) {
      throw ImmersveError.create({ name: 'KycSessionMissingStatus' });
    }
    return data;
  }

  async getExternalKycSessionToken(sessionId: string): Promise<ExternalSessionToken> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const { data } = await this.axiosInstance({
        method: 'POST',
        url: `/api/kyc-sessions/${sessionId}/external-session-token`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionGetExternalTokenFailed',
        cause: err as Error,
      });
    }
  }

  async completeKycSession(sessionId: string): Promise<void> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      await this.axiosInstance({
        method: 'POST',
        url: `/api/kyc-sessions/${sessionId}/complete`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionCompleteFailed',
        cause: err as Error,
      });
    }
  }

  async startKycSession(sessionId: string): Promise<void> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      await this.axiosInstance({
        method: 'POST',
        url: `/api/kyc-sessions/${sessionId}/start`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionStartFailed',
        cause: err as Error,
      });
    }
  }

  async pushExternalProfile(sessionId: string): Promise<void> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      await this.axiosInstance({
        method: 'POST',
        url: `/api/kyc-sessions/${sessionId}/push-external-profile`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionPushExternalProfileFailed',
        cause: err as Error,
      });
    }
  }

  async getSupportedRegions(accountId: string): Promise<SupportedRegionsResponseBody | undefined> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const response = await this.axiosInstance<SupportedRegionsResponseBody>({
        method: 'GET',
        url: `/api/accounts/${accountId}/supported-regions`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'GetSupportedRegionsFailed',
        cause: err as Error,
      });
    }
  }

  async updateKycSessionRegion(sessionId: string, regionCode: string): Promise<void> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      await this.axiosInstance({
        method: 'POST',
        url: `/api/kyc-sessions/${sessionId}/update-region`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        data: {
          region: regionCode,
        },
      });
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionUpdateRegionFailed',
        cause: err as Error,
      });
    }
  }

  async getContactChannels(accountId: string): Promise<ContactChannelsResponseBody | undefined> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const { data } = await this.axiosInstance<ContactChannelsResponseBody>({
        method: 'GET',
        url: `/api/accounts/${accountId}/contact-details`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'GetContactChannelsFailed',
        cause: err as Error,
      });
    }
  }

  async getResidentialAddresses(
    accountId: string
  ): Promise<ResidentialAddressesResponseBody | undefined> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const { data } = await this.axiosInstance<ResidentialAddressesResponseBody>({
        method: 'GET',
        url: `/api/accounts/${accountId}/residential-addresses`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'GetResidentialAddressesFailed',
        cause: err as Error,
      });
    }
  }

  async updateResidentialAddresses(props: {
    accountId: string;
    residentialAddress: StructuredResidentialAddressDto | UnstructuredResidentialAddressDto;
  }): Promise<ResidentialAddressesResponseBody> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const { data } = await this.axiosInstance<ResidentialAddressesResponseBody>({
        method: 'POST',
        url: `/api/accounts/${props.accountId}/residential-addresses`,
        data: [props.residentialAddress],
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });

      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'UpdateResidentialAddressesFailed',
        cause: err as Error,
      });
    }
  }

  async updateContactChannels(
    props: UpdateContactChannelsProps
  ): Promise<ContactChannelsResponseBody | undefined> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    const body: UpdateContactChannelsBody = {
      email: {
        emailAddress: props.emailAddress,
      },
      phone: {
        phoneNumber: props.phoneNumber,
      },
    };

    try {
      const { data } = await this.axiosInstance<ContactChannelsResponseBody>({
        method: 'PATCH',
        url: `/api/accounts/${props.accountId}/contact-details`,
        data: body,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'UpdateContactChannelsFailed',
        cause: err as Error,
      });
    }
  }

  async getExpectedSpend(cardholderId: string): Promise<ExpectedSpendResponseBody | undefined> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const { data } = await this.axiosInstance<ExpectedSpendResponseBody>({
        method: 'GET',
        url: `/api/accounts/${cardholderId}/expected-spend-amount`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'GetExpectedSpendAmountFailed',
        cause: err as Error,
      });
    }
  }

  async updateExpectedSpend(
    props: UpdateExpectedSpendProps
  ): Promise<ExpectedSpendResponseBody | undefined> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    const body: UpdateExpectedSpendBody = {
      expectedSpendAmount: props.expectedSpendAmount,
    };

    try {
      const { data } = await this.axiosInstance<ExpectedSpendResponseBody>({
        method: 'POST',
        url: `/api/accounts/${props.cardholderId}/expected-spend-amount`,
        data: body,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'UpdateExpectedSpendAmountFailed',
        cause: err as Error,
      });
    }
  }

  async uploadFile(props: UploadFileProps): Promise<UploadFileResponseBody> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    const formData = new FormData();
    formData.append('accountId', props.cardholderId);
    formData.append('purpose', 'kyc-doc-upload');
    formData.append('file', props.file);

    try {
      const { data } = await this.axiosInstance<UploadFileResponseBody>({
        method: 'POST',
        url: '/api/file-uploads',
        data: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'UploadFileFailed',
        cause: err as Error,
      });
    }
  }

  async getFileUpload(fileUploadId: string): Promise<UploadFileResponseBody> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    try {
      const { data } = await this.axiosInstance<UploadFileResponseBody>({
        method: 'GET',
        url: `/api/file-uploads/${fileUploadId}`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return data;
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'GetFileUploadFailed',
        cause: err as Error,
      });
    }
  }

  async attachKycSessionDocument(props: AttachKycDocumentProps): Promise<void> {
    const authStore = useImmersveAuthStore();
    const accessToken = authStore.accessToken;

    const body: AttachKycDocumentBody = {
      fileId: props.fileId,
    };
    try {
      await this.axiosInstance({
        method: 'POST',
        url: `/api/kyc-sessions/${props.sessionId}/attach/${props.attachmentType}`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        data: body,
      });
    } catch (err) {
      throw ImmersveError.fromCause({
        name: 'KycSessionAttachDocumentFailed',
        cause: err as Error,
      });
    }
  }
}
